import { createFileRoute, useNavigate, getRouteApi, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, useRef, useEffect } from "react";

import { api } from "../../../lib/api";
import { ProviderLogo } from "../../../components/shared/ProviderLogo";
import { DialogPanel } from "../../../components/shared/DialogPanel";
import { toastError } from "../../../components/shared/toast";
import { AlertTriangleIcon, ClockIcon, XIcon } from "../../../components/shared/Icons";
import { backdropClose } from "../../../components/shared/backdrop";

const _authedRoute = getRouteApi("/_authed");

interface Agent {
  id: string;
  name: string;
  model_name: string;
  model_provider: string;
  status: string;
  tags: string[];
  manager_id: string | null;
  manager_name: string | null;
  participant_count?: number;
  template_id?: string | null;  // Self-Service: implantado a partir de um global
  folder_id?: string | null;
  folder_name?: string | null;
  created_at: string;
  updated_at: string;
}

interface User {
  id: string;
  name: string;
}

interface Folder {
  id: string;
  name: string;
  owner_id: string;
  agent_count: number;
  last_agent_updated_at: string | null;
  can_manage: boolean;
}

export const Route = createFileRoute("/_authed/agents/")({
  component: AgentsPage,
  validateSearch: (s: Record<string, unknown>): { alerting?: boolean; pending?: boolean; unpublished?: boolean } => ({
    alerting: s.alerting === true || s.alerting === "1" || s.alerting === "true" ? true : undefined,
    pending: s.pending === true || s.pending === "1" || s.pending === "true" ? true : undefined,
    unpublished: s.unpublished === true || s.unpublished === "1" || s.unpublished === "true" ? true : undefined,
  }),
});

const STATUS_LABEL: Record<string, string> = {
  active: "Ativo",
  development: "Em desenvolvimento",
  inactive: "Inativo",
};
const STATUS_STYLE: Record<string, { bg: string; color: string; border: string }> = {
  active:      { bg: "var(--ok-soft)",  color: "var(--ok)", border: "var(--ok-line)" },
  development: { bg: "var(--info-soft)",  color: "var(--info)", border: "var(--info-line)" },
  inactive:    { bg: "var(--surface-card)",  color: "var(--ink-muted)", border: "var(--line-strong)"   },
};

// ponytail: só rótulos bonitos; a lista de opções vem dos agentes (sempre em sincronia)
const PROVIDER_LABEL: Record<string, string> = {
  openai: "OpenAI", anthropic: "Anthropic", google: "Google",
  grok: "Grok", openrouter: "OpenRouter", deepseek: "DeepSeek", meta: "Meta",
};

const inputStyle: React.CSSProperties = {
  padding: "7px 12px",
  background: "var(--surface-input)",
  border: "1px solid var(--line)",
  borderRadius: 8,
  color: "var(--ink)",
  fontSize: 13,
  outline: "none",
};

// Ícone de pasta reutilizado nos cards de pasta / botão criar.
function FolderIcon({ size = 14, color = "var(--folder-icon)" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
      <path d="M4 20h16a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-7.9a2 2 0 0 1-1.69-.9L9.6 3.9A2 2 0 0 0 7.93 3H4a2 2 0 0 0-2 2v13c0 1.1.9 2 2 2Z" />
    </svg>
  );
}

// ── Tag dropdown ──────────────────────────────────────────────────────────
function TagDropdown({ allTags, activeTags, onToggle }: {
  allTags: string[];
  activeTags: Set<string>;
  onToggle: (t: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const filtered = allTags.filter(t => t.toLowerCase().includes(search.toLowerCase()));
  const count = activeTags.size;

  return (
    <div ref={ref} style={{ position: "relative" }}>
      <button
        onClick={() => setOpen(o => !o)}
        style={{
          ...inputStyle,
          display: "flex", alignItems: "center", gap: 6, cursor: "pointer",
          background: count > 0 ? "var(--brand-soft)" : "var(--surface-input)",
          border: `1px solid ${count > 0 ? "var(--brand)" : "var(--line)"}`,
          color: count > 0 ? "var(--brand)" : "var(--ink-muted)",
          minWidth: 120,
        }}
      >
        <span>Tags{count > 0 ? ` (${count})` : ""}</span>
        <svg style={{ marginLeft: "auto", flexShrink: 0 }} width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: "absolute", top: "calc(100% + 4px)", right: 0, zIndex: "var(--z-dropdown)" as any,
          background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10,
          width: 220, boxShadow: "var(--shadow-pop)",
        }}>
          <div style={{ padding: "8px 10px", borderBottom: "1px solid var(--line)" }}>
            <input
              autoFocus
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Buscar tag..."
              style={{ ...inputStyle, width: "100%", boxSizing: "border-box", padding: "5px 8px", fontSize: 12 }}
            />
          </div>
          <div style={{ maxHeight: 200, overflowY: "auto" }}>
            {filtered.length === 0 ? (
              <div style={{ padding: "10px 12px", fontSize: 12, color: "var(--ink-dim)" }}>Nenhuma tag</div>
            ) : (
              filtered.map(t => (
                <label key={t} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 12px", cursor: "pointer" }}
                  onMouseEnter={e => (e.currentTarget.style.background = "var(--surface-card)")}
                  onMouseLeave={e => (e.currentTarget.style.background = "transparent")}
                >
                  <input type="checkbox" checked={activeTags.has(t)} onChange={() => onToggle(t)}
                    style={{ accentColor: "var(--brand)", width: 13, height: 13, flexShrink: 0 }} />
                  <span style={{ fontSize: 13, color: "var(--ink-secondary)" }}>{t}</span>
                </label>
              ))
            )}
          </div>
          {count > 0 && (
            <div style={{ padding: "6px 12px", borderTop: "1px solid var(--line)" }}>
              <button
                onClick={() => { activeTags.forEach(t => onToggle(t)); }}
                style={{ fontSize: 11, color: "var(--ink-muted)", background: "none", border: "none", cursor: "pointer", padding: 0 }}
              >
                Limpar filtro
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────
function AgentsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const isViewer = !!(_authedRoute.useRouteContext() as { user?: { permissions?: Record<string, boolean> } }).user?.permissions?.viewer;
  const { alerting: alertingFilter, pending: pendingFilter, unpublished: unpublishedFilter } = Route.useSearch();
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState("");
  const [managerId, setManagerId] = useState("");
  const [activeTags, setActiveTags] = useState<Set<string>>(new Set());
  const [showInactive, setShowInactive] = useState(false);
  const [kind, setKind] = useState<"" | "simple" | "full">("");
  const [openFolderId, setOpenFolderId] = useState<string | null>(null);
  const [folderForm, setFolderForm] = useState<{ mode: "create" | "rename"; id?: string; name: string } | null>(null);
  const [deleteFolder, setDeleteFolder] = useState<Folder | null>(null);
  const [sortBy, setSortBy] = useState<"updated" | "created_asc" | "created_desc">("updated");

  const { data: agents = [] } = useQuery<Agent[]>({
    queryKey: ["agents"],
    queryFn: () => api.get("/agents"),
  });

  const { data: users = [] } = useQuery<User[]>({
    queryKey: ["users"],
    queryFn: () => api.get("/users"),
  });

  const { data: folders = [] } = useQuery<Folder[]>({
    queryKey: ["agent-folders"],
    queryFn: () => api.get("/agents/folders"),
  });

  const { data: health } = useQuery<{ degraded: string[] }>({
    queryKey: ["agents-health"],
    queryFn: () => api.get("/agents/health"),
    refetchInterval: 60_000,
  });
  const degradedSet = new Set(health?.degraded ?? []);

  const { data: alerts, isError: alertsError } = useQuery<{ active: string[]; pending: string[] }>({
    queryKey: ["agents-alerts"],
    queryFn: () => api.get("/agents/alerts"),
    refetchInterval: 20_000,
  });
  const alertingSet = new Set(alerts?.active ?? []);
  const pendingSet = new Set(alerts?.pending ?? []);

  const { data: unpub } = useQuery<{ unpublished: string[] }>({
    queryKey: ["agents-unpublished"],
    queryFn: () => api.get("/agents/unpublished"),
    refetchInterval: 60_000,
  });
  const unpublishedSet = new Set(unpub?.unpublished ?? []);

  const invalidateFolders = () => {
    qc.invalidateQueries({ queryKey: ["agent-folders"] });
    qc.invalidateQueries({ queryKey: ["agents"] });
  };
  const folderErr = (fallback: string) => (err: unknown) =>
    toastError((err as any)?.payload?.detail ?? fallback);
  const createMut = useMutation({
    mutationFn: (name: string) => api.post("/agents/folders", { name }),
    onSuccess: () => { invalidateFolders(); setFolderForm(null); },
    onError: folderErr("Falha ao criar a pasta."),
  });
  const renameMut = useMutation({
    mutationFn: (v: { id: string; name: string }) => api.patch(`/agents/folders/${v.id}`, { name: v.name }),
    onSuccess: () => { invalidateFolders(); setFolderForm(null); },
    onError: folderErr("Falha ao renomear a pasta."),
  });
  const deleteMut = useMutation({
    mutationFn: (id: string) => api.delete(`/agents/folders/${id}`),
    onSuccess: () => { invalidateFolders(); setDeleteFolder(null); setOpenFolderId(null); },
    onError: folderErr("Falha ao excluir a pasta."),
  });
  // Drop de badge de agente numa pasta (colocação POR USUÁRIO, endpoint existente)
  const moveMut = useMutation({
    mutationFn: (v: { agentId: string; folderId: string }) =>
      api.put(`/agents/${v.agentId}/folder`, { folder_id: v.folderId }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["agents"] }); invalidateFolders(); },
    onError: folderErr("Falha ao mover o agente de pasta."),
  });

  // Ordem manual das pastas: persistida no banco (segue o usuário em qualquer máquina)
  const orderMut = useMutation({
    mutationFn: (ids: string[]) => api.put("/agents/folders/order", { folder_ids: ids }),
    onSuccess: invalidateFolders,
    onError: folderErr("Falha ao salvar a ordem das pastas."),
  });

  const allTags = Array.from(new Set(agents.flatMap((a) => a.tags ?? []))).sort();
  const allProviders = Array.from(new Set(agents.map((a) => a.model_provider).filter(Boolean))).sort();

  function toggleTag(tag: string) {
    setActiveTags((prev) => {
      const next = new Set(prev);
      if (next.has(tag)) next.delete(tag);
      else next.add(tag);
      return next;
    });
  }

  // Busca: nome igual > começa com > contém (id só entra como "contém"); empate = ordenação escolhida.
  const searchRank = (a: Agent) => {
    const q = search.trim().toLowerCase();
    if (!q) return 0;
    const n = a.name.toLowerCase();
    return n === q ? 0 : n.startsWith(q) ? 1 : n.includes(q) ? 2 : 3;
  };
  const byFilter = (a: Agent, b: Agent) => {
    const r = searchRank(a) - searchRank(b);
    if (r !== 0) return r;
    if (sortBy === "created_asc") return +new Date(a.created_at) - +new Date(b.created_at);
    if (sortBy === "created_desc") return +new Date(b.created_at) - +new Date(a.created_at);
    return +new Date(b.updated_at) - +new Date(a.updated_at);  // última atualização primeiro
  };

  const filtered = agents.filter((a) => {
    // Só filtra por alerta quando os dados de alerta já chegaram (senão esconderia tudo).
    if (alertingFilter && alerts && !alertingSet.has(a.id)) return false;
    if (pendingFilter && alerts && !pendingSet.has(a.id)) return false;
    if (unpublishedFilter && unpub && !unpublishedSet.has(a.id)) return false;
    if (!showInactive && a.status === "inactive") return false;
    if (kind === "simple" && !a.template_id) return false;
    if (kind === "full" && a.template_id) return false;
    if (search && !a.name.toLowerCase().includes(search.toLowerCase()) && !a.id.toLowerCase().includes(search.toLowerCase())) return false;
    if (provider && a.model_provider !== provider) return false;
    if (managerId && a.manager_id !== managerId) return false;
    if (activeTags.size > 0 && !a.tags?.some((t) => activeTags.has(t))) return false;
    return true;
  }).sort(byFilter);

  const q = search.trim().toLowerCase();
  // Drill-down do dashboard (alertas / não publicados): lista plana de TODOS os
  // agentes que batem, com ou sem pasta — as seções de pasta ficam de fora.
  const isDrilldown = !!(alertingFilter || pendingFilter || unpublishedFilter);
  // Qualquer filtro/ordenação manual "solta" os agentes das pastas: se estou
  // filtrando, não faz sentido o agente ficar preso na pasta. Sem filtro =
  // escopo normal (pastas + soltos). O sort padrão ("updated" = última
  // atualização) é o repouso, só conta se for trocado.
  const hasActiveFilter =
    q !== "" || showInactive || kind !== "" || provider !== "" || managerId !== "" ||
    activeTags.size > 0 || sortBy !== "updated";
  // Lista plana (sem seções de pasta): drill-down do dashboard OU filtro ativo.
  const flatList = isDrilldown || hasActiveFilter;
  // O backend já devolve na ordem (position → alfabética); aqui só reordena e grava.
  function reorderFolders(dragId: string, targetId: string) {
    const ids = folders.map((f) => f.id);
    const from = ids.indexOf(dragId), to = ids.indexOf(targetId);
    if (from === -1 || to === -1 || from === to) return;
    ids.splice(to, 0, ...ids.splice(from, 1));
    orderMut.mutate(ids);
  }
  const foldersFiltered = folders.filter((f) => !q || f.name.toLowerCase().includes(q));
  // folder_id/folder_name vêm POR USUÁRIO (a minha colocação do agente). "Solto" =
  // agente que eu não coloquei em nenhuma pasta minha.
  const looseAgents = filtered.filter((a) => !a.folder_id);
  const openFolder = folders.find((f) => f.id === openFolderId) ?? null;
  // Dentro da pasta: TODOS os agentes visíveis dela (qualquer status), só filtrando
  // por busca — assim a grade bate com a contagem do card.
  const agentsInOpenFolder = openFolderId
    ? agents
        .filter((a) => a.folder_id === openFolderId && (!q || a.name.toLowerCase().includes(q) || a.id.toLowerCase().includes(q)))
        .sort(byFilter)
    : [];

  // Entrar/sair de pasta limpa a busca — senão o texto filtraria a lista errada.
  function openFolderView(id: string) { setSearch(""); setOpenFolderId(id); }
  function closeFolderView() { setSearch(""); setOpenFolderId(null); }

  const gridStyle: React.CSSProperties = { display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: 16 };

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
      {/* Topbar */}
      <div style={{ height: 52, borderBottom: "1px solid var(--line)", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "0 24px", flexShrink: 0 }}>
        <h1 style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", margin: 0 }}>Agentes</h1>
        {!isViewer && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <button
            onClick={() => setFolderForm({ mode: "create", name: "" })}
            style={{ background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line-strong)", padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}
          >
            <FolderIcon size={14} color="var(--ink-secondary)" /> Criar pasta
          </button>
          <Link
            to="/agents/new"
            style={{ background: "var(--brand)", color: "#fff", padding: "7px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, textDecoration: "none" }}
          >
            + Novo agente
          </Link>
        </div>
        )}
      </div>

      {/* Filter bar */}
      <div style={{ padding: "12px 24px", borderBottom: "1px solid var(--line)", display: "flex", gap: 10, alignItems: "center", flexShrink: 0, flexWrap: "wrap" }}>
        <input
          style={{ ...inputStyle, flex: "1 1 200px" }}
          placeholder={openFolder ? "Buscar nesta pasta..." : "Buscar por nome, ID ou pasta..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />

        {/* Toggle mostrar inativos */}
        <button
          onClick={() => setShowInactive(v => !v)}
          style={{
            display: "flex", alignItems: "center", gap: 7,
            padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
            background: showInactive ? "var(--brand-soft)" : "var(--surface-input)",
            border: `1px solid ${showInactive ? "var(--brand)" : "var(--line)"}`,
            color: showInactive ? "var(--brand)" : "var(--ink-muted)",
            flexShrink: 0,
          }}
        >
          <span style={{
            display: "inline-block", width: 26, height: 14, borderRadius: 99,
            background: showInactive ? "var(--brand)" : "var(--line-bold)",
            position: "relative", transition: "background .15s", flexShrink: 0,
          }}>
            <span style={{
              position: "absolute", top: 2, width: 10, height: 10, borderRadius: "50%",
              background: "#fff", left: showInactive ? 14 : 2, transition: "left .15s",
            }} />
          </span>
          Mostrar inativos
        </button>

        <select style={{ ...inputStyle, minWidth: 140 }} value={managerId} onChange={e => setManagerId(e.target.value)}>
          <option value="">Todos os gestores</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>

        <select style={{ ...inputStyle, minWidth: 130 }} value={provider} onChange={e => setProvider(e.target.value)}>
          <option value="">Todos os modelos</option>
          {allProviders.map((p) => <option key={p} value={p}>{PROVIDER_LABEL[p] ?? p}</option>)}
        </select>

        <select style={{ ...inputStyle, minWidth: 130 }} value={kind} onChange={e => setKind(e.target.value as typeof kind)}>
          <option value="">Todos os tipos</option>
          <option value="simple">Simplificado</option>
          <option value="full">Completo</option>
        </select>

        <select style={{ ...inputStyle, minWidth: 150 }} value={sortBy} onChange={e => setSortBy(e.target.value as typeof sortBy)}>
          <option value="updated">Última atualização</option>
          <option value="created_asc">Ordem de criação ↑</option>
          <option value="created_desc">Ordem de criação ↓</option>
        </select>

        {allTags.length > 0 && (
          <TagDropdown allTags={allTags} activeTags={activeTags} onToggle={toggleTag} />
        )}

        {alertingFilter && (
          <button
            onClick={() => navigate({ to: "/agents", search: {} })}
            className="alert-pulse"
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
              background: "var(--brand-soft)", border: "1px solid var(--danger)", color: "var(--danger)",
              fontWeight: 600, flexShrink: 0,
            }}
          >
            <AlertTriangleIcon size={13} /> Só com alertas de erro <XIcon size={12} />
          </button>
        )}

        {pendingFilter && (
          <button
            onClick={() => navigate({ to: "/agents", search: {} })}
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
              background: "var(--pending-soft)", border: "1px solid var(--pending)", color: "var(--pending)",
              fontWeight: 600, flexShrink: 0,
            }}
          >
            <ClockIcon size={13} /> Só aguardando aprovação <XIcon size={12} />
          </button>
        )}

        {unpublishedFilter && (
          <button
            onClick={() => navigate({ to: "/agents", search: {} })}
            className="publish-pulse"
            style={{
              display: "flex", alignItems: "center", gap: 7,
              padding: "7px 12px", borderRadius: 8, fontSize: 12, cursor: "pointer",
              background: "var(--warn-soft)", border: "1px solid var(--warn-line)", color: "var(--warn)",
              fontWeight: 600, flexShrink: 0,
            }}
          >
            <AlertTriangleIcon size={13} /> Só não publicados <XIcon size={12} />
          </button>
        )}
      </div>

      {/* Content area */}
      <div style={{ flex: 1, overflowY: "auto", padding: "24px" }}>
        {openFolder ? (
          // Pasta aberta: os agentes dela
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <button
                onClick={closeFolderView}
                style={{ background: "none", border: "none", color: "var(--ink-muted)", cursor: "pointer", fontSize: 13, display: "flex", alignItems: "center", gap: 4, padding: 0 }}
              >
                ← Voltar
              </button>
              <FolderIcon size={18} />
              <span style={{ fontSize: 16, fontWeight: 700, color: "var(--ink)" }}>{openFolder.name}</span>
              {openFolder.can_manage && (
                <div style={{ display: "flex", gap: 6, marginLeft: 4 }}>
                  <button
                    onClick={() => setFolderForm({ mode: "rename", id: openFolder.id, name: openFolder.name })}
                    style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--line-strong)", background: "var(--surface-input)", color: "var(--ink-secondary)", cursor: "pointer" }}
                  >
                    Renomear
                  </button>
                  <button
                    onClick={() => setDeleteFolder(openFolder)}
                    style={{ fontSize: 12, padding: "4px 10px", borderRadius: 6, border: "1px solid var(--danger-soft-line)", background: "var(--danger-soft)", color: "var(--danger)", cursor: "pointer" }}
                  >
                    Excluir pasta
                  </button>
                </div>
              )}
            </div>
            {agentsInOpenFolder.length === 0 ? (
              <div style={{ fontSize: 13, color: "var(--ink-muted)" }}>Nenhum agente nesta pasta.</div>
            ) : (
              <div style={gridStyle}>
                {agentsInOpenFolder.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} degraded={degradedSet.has(agent.id)} alerting={alertingSet.has(agent.id)} pending={pendingSet.has(agent.id)} unpublished={unpublishedSet.has(agent.id)} />
                ))}
              </div>
            )}
          </div>
        ) : flatList ? (
          // Lista plana: drill-down do dashboard ou filtro ativo (com ou sem pasta)
          (alertingFilter || pendingFilter) && !alerts ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, color: "var(--ink-muted)" }}>
              <span style={{ fontSize: 13 }}>{alertsError ? "Não foi possível carregar os alertas." : "Carregando alertas…"}</span>
            </div>
          ) : filtered.length === 0 ? (
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, color: "var(--ink-muted)" }}>
              <span style={{ fontSize: 13 }}>{isDrilldown ? (alertingFilter ? "Nenhum agente em alerta" : pendingFilter ? "Nenhum agente aguardando aprovação" : "Nenhum agente não publicado") : "Nenhum agente encontrado"}</span>
            </div>
          ) : (
            <div style={gridStyle}>
              {filtered.map((agent) => (
                <AgentCard key={agent.id} agent={agent} degraded={degradedSet.has(agent.id)} alerting={alertingSet.has(agent.id)} pending={pendingSet.has(agent.id)} unpublished={unpublishedSet.has(agent.id)} showFolder />
              ))}
            </div>
          )
        ) : foldersFiltered.length === 0 && looseAgents.length === 0 ? (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", height: "100%", gap: 12, color: "var(--ink-muted)" }}>
            <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ opacity: 0.25 }}>
              <circle cx="12" cy="8" r="4" />
              <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
            </svg>
            <span style={{ fontSize: 13 }}>Nenhum agente encontrado</span>
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
            {/* Pastas — sempre no topo, ordem alfabética (vem do backend) */}
            {foldersFiltered.length > 0 && (
              <div style={gridStyle}>
                {foldersFiltered.map((f) => (
                  <FolderCard
                    key={f.id}
                    folder={f}
                    onOpen={() => openFolderView(f.id)}
                    onDropAgent={(agentId) => moveMut.mutate({ agentId, folderId: f.id })}
                    onReorder={(dragId) => reorderFolders(dragId, f.id)}
                  />
                ))}
              </div>
            )}

            {/* separador entre pastas e agentes soltos */}
            {foldersFiltered.length > 0 && looseAgents.length > 0 && <div style={{ height: 1, background: "var(--line)" }} />}

            {/* Agentes soltos — só os que não estão em pasta, na ordem do filtro */}
            {looseAgents.length > 0 && (
              <div style={gridStyle}>
                {looseAgents.map((agent) => (
                  <AgentCard key={agent.id} agent={agent} degraded={degradedSet.has(agent.id)} alerting={alertingSet.has(agent.id)} pending={pendingSet.has(agent.id)} unpublished={unpublishedSet.has(agent.id)} />
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {folderForm && (
        <FolderFormModal
          key={folderForm.mode + (folderForm.id ?? "")}
          form={folderForm}
          pending={createMut.isPending || renameMut.isPending}
          error={(createMut.error as { payload?: { detail?: string } } | null)?.payload?.detail
            ?? (renameMut.error as { payload?: { detail?: string } } | null)?.payload?.detail}
          onCancel={() => { setFolderForm(null); createMut.reset(); renameMut.reset(); }}
          onSubmit={(name) => folderForm.mode === "create" ? createMut.mutate(name) : renameMut.mutate({ id: folderForm.id!, name })}
        />
      )}

      {deleteFolder && (
        <ConfirmModal
          title="Excluir pasta?"
          message={`A pasta "${deleteFolder.name}" será excluída. Os ${deleteFolder.agent_count} agente(s) dentro dela NÃO são apagados — apenas saem da pasta.`}
          confirmLabel={deleteMut.isPending ? "Excluindo..." : "Sim, excluir"}
          pending={deleteMut.isPending}
          onCancel={() => setDeleteFolder(null)}
          onConfirm={() => deleteMut.mutate(deleteFolder.id)}
        />
      )}
    </div>
  );
}

// ── Card ──────────────────────────────────────────────────────────────────
function fmtUpdated(iso: string): string {
  return new Date(iso).toLocaleString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
    timeZone: "America/Sao_Paulo",
  });
}

function AgentCard({ agent, degraded, alerting, pending, unpublished, showFolder = false }: { agent: Agent; degraded: boolean; alerting: boolean; pending: boolean; unpublished: boolean; showFolder?: boolean }) {
  const [hovered, setHovered] = useState(false);
  const ss = STATUS_STYLE[agent.status] ?? STATUS_STYLE.inactive;
  // Rótulo amigável do modelo (catálogo dev); a query é deduplicada pela key,
  // então todos os cards compartilham um único fetch. Fallback: nome técnico.
  const { data: modelCatalog = [] } = useQuery<{ model_name: string; label: string }[]>({
    queryKey: ["available-models"],
    queryFn: () => api.get("/dev/available-models"),
  });
  const modelLabel = modelCatalog.find((m) => m.model_name === agent.model_name)?.label ?? agent.model_name;

  const inactive = agent.status === "inactive";
  return (
    <Link
      to="/agents/$agentId"
      params={{ agentId: agent.id }}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/agent-id", agent.id); }}
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className={alerting ? "alert-pulse" : undefined}
        style={{
          background: hovered ? "var(--surface-card)" : "var(--surface-hover)",
          border: `1px solid ${alerting ? "var(--danger)" : pending ? "var(--pending)" : hovered ? "var(--line-max)" : degraded ? "var(--warn-line)" : "var(--line)"}`,
          borderRadius: 10,
          padding: 18,
          display: "flex",
          flexDirection: "column",
          gap: 8,
          transition: "border-color .15s, background .15s, opacity .15s",
          // Desativado: fade bem mais escuro pra diferenciar de cara
          opacity: inactive && !hovered ? 0.45 : 1,
          filter: inactive && !hovered ? "grayscale(0.7)" : "none",
        }}
      >
        {/* Pasta de origem — só na lista plana (filtro/drill-down), pra saber de onde o agente veio */}
        {showFolder && agent.folder_name && (
          <div style={{ alignSelf: "flex-start", maxWidth: "100%", display: "inline-flex", alignItems: "center", gap: 4, fontSize: 10.5, color: "var(--ink-muted)", background: "var(--surface-input)", border: "1px solid var(--line)", borderRadius: 99, padding: "1px 8px 1px 6px" }}>
            <FolderIcon size={11} color="var(--ink-muted)" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{agent.folder_name}</span>
          </div>
        )}

        {/* Nome */}
        <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)", lineHeight: 1.3 }}>
          {agent.name}
        </div>

        {/* Provider / modelo */}
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <ProviderLogo provider={agent.model_provider} size={20} />
          <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>{modelLabel}</span>
          <span style={{ fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 99, whiteSpace: "nowrap", background: agent.template_id ? "var(--warn-soft)" : "var(--surface-input)", border: `1px solid ${agent.template_id ? "var(--warn-line)" : "var(--line)"}`, color: agent.template_id ? "var(--warn)" : "var(--ink-muted)" }}>
            {agent.template_id ? "Simplificado" : "Completo"}
          </span>
          {alerting && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 99, background: "var(--danger-soft)", border: "1px solid var(--danger)", color: "var(--danger)" }}>
              <AlertTriangleIcon size={10} /> Erros
            </span>
          )}
          {pending && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 99, background: "var(--pending-soft)", border: "1px solid var(--pending)", color: "var(--pending)" }}>
              <ClockIcon size={10} /> Pendente
            </span>
          )}
          {degraded && (
            <span style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", fontSize: 10, padding: "2px 6px", borderRadius: 99, background: "var(--pending-soft)", border: "1px solid var(--warn-line)", color: "var(--orange)" }}>
              <AlertTriangleIcon size={10} /> Degradado
            </span>
          )}
          {unpublished && (
            <span className="publish-pulse" style={{ display: "inline-flex", alignItems: "center", gap: 3, whiteSpace: "nowrap", fontSize: 10, fontWeight: 600, padding: "2px 6px", borderRadius: 99, background: "var(--warn-soft)", border: "1px solid var(--warn-line)", color: "var(--warn)" }}>
              <AlertTriangleIcon size={10} /> Publicar
            </span>
          )}
        </div>

        {/* Footer: gestor — status / tags — atualizado (altura fixa, com ou sem tags) */}
        <div style={{ borderTop: "1px solid var(--surface-input)", paddingTop: 8, marginTop: 2, display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>
              {agent.manager_name ?? "—"}
              {agent.participant_count ? <span style={{ color: "var(--warn)", fontWeight: 600 }}> +{agent.participant_count}</span> : null}
            </span>
            <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: ss.bg, color: ss.color, border: `1px solid ${ss.border}` }}>
              {STATUS_LABEL[agent.status] ?? agent.status}
            </span>
          </div>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8, minHeight: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, overflow: "hidden" }}>
              {(agent.tags ?? []).slice(0, 2).map(tag => (
                <span key={tag} title={tag} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-input)", color: "var(--ink-dim)", border: "1px solid var(--line)", maxWidth: 90, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {tag}
                </span>
              ))}
              {(agent.tags?.length ?? 0) > 2 && (
                <span title={agent.tags.slice(2).join(", ")} style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-input)", color: "var(--ink-muted)", border: "1px solid var(--line)", flexShrink: 0, cursor: "default" }}>
                  +{agent.tags.length - 2}
                </span>
              )}
            </div>
            <span style={{ fontSize: 10.5, color: "var(--ink-dim)", flexShrink: 0, whiteSpace: "nowrap" }}>
              Atualizado {fmtUpdated(agent.updated_at)}
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
}

// ── Folder card ─────────────────────────────────────────────────────────────
// Renomear/excluir ficam só dentro da pasta aberta — o card apenas abre.
// Compacto (1 linha: ícone · nome · contagem). Aceita drop de agente (move pra
// pasta) e de outra pasta (reordena); arrastável pra reordenar.
function FolderCard({ folder, onOpen, onDropAgent, onReorder }: {
  folder: Folder;
  onOpen: () => void;
  onDropAgent: (agentId: string) => void;
  onReorder: (dragFolderId: string) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  return (
    <div
      onClick={onOpen}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      draggable
      onDragStart={(e) => { e.dataTransfer.setData("text/folder-id", folder.id); }}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const agentId = e.dataTransfer.getData("text/agent-id");
        const folderId = e.dataTransfer.getData("text/folder-id");
        // Só o dono da pasta coloca agente nela (a API exige owner) — evita drop mudo
        if (agentId && folder.can_manage) onDropAgent(agentId);
        else if (folderId && folderId !== folder.id) onReorder(folderId);
      }}
      style={{
        background: dragOver ? "var(--brand-soft)" : "var(--folder-card-bg)",
        border: `1px solid ${dragOver ? "var(--brand)" : hovered ? "var(--folder-card-line-hover)" : "var(--folder-card-line)"}`,
        borderRadius: 10, padding: "10px 14px", cursor: "pointer",
        display: "flex", alignItems: "center", gap: 8,
        transition: "border-color .15s, background .15s",
      }}
    >
      <FolderIcon size={16} />
      <span style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{folder.name}</span>
      <span style={{ fontSize: 11, padding: "2px 8px", borderRadius: 99, background: "var(--surface-input)", color: "var(--ink-muted)", border: "1px solid var(--line)", flexShrink: 0 }}>
        {folder.agent_count} agente{folder.agent_count === 1 ? "" : "s"}
      </span>
    </div>
  );
}

// ── Modais ────────────────────────────────────────────────────────────────
function Overlay({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  return (
    <div
      {...backdropClose(() => { onClose(); })}
      style={{ position: "fixed", inset: 0, background: "var(--overlay)", zIndex: "var(--z-modal)" as any, display: "flex", alignItems: "center", justifyContent: "center" }}
    >
      {children}
    </div>
  );
}

function FolderFormModal({ form, pending, error, onSubmit, onCancel }: {
  form: { mode: "create" | "rename"; name: string };
  pending: boolean; error?: string;
  onSubmit: (name: string) => void; onCancel: () => void;
}) {
  const [name, setName] = useState(form.name);
  const submit = () => { if (name.trim()) onSubmit(name.trim()); };
  return (
    <Overlay onClose={onCancel}>
      <DialogPanel onClose={onCancel} label={form.mode === "create" ? "Criar pasta" : "Renomear pasta"} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 400, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{form.mode === "create" ? "Criar pasta" : "Renomear pasta"}</div>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") submit(); }}
          placeholder="Nome da pasta"
          style={{ ...inputStyle, width: "100%", boxSizing: "border-box" }}
        />
        {error && <span style={{ fontSize: 12, color: "var(--danger)" }}>{error}</span>}
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={submit} disabled={pending || !name.trim()} style={{ background: "var(--brand)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: pending || !name.trim() ? 0.6 : 1 }}>
            {pending ? "Salvando..." : form.mode === "create" ? "Criar" : "Salvar"}
          </button>
        </div>
      </DialogPanel>
    </Overlay>
  );
}

function ConfirmModal({ title, message, confirmLabel, pending, onConfirm, onCancel }: {
  title: string; message: string; confirmLabel: string; pending: boolean;
  onConfirm: () => void; onCancel: () => void;
}) {
  return (
    <Overlay onClose={onCancel}>
      <DialogPanel onClose={onCancel} role="alertdialog" label={title} style={{ background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 12, width: 420, padding: 24, display: "flex", flexDirection: "column", gap: 16 }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>{title}</div>
        <div style={{ fontSize: 13, color: "var(--ink-secondary)", lineHeight: 1.5 }}>{message}</div>
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
          <button onClick={onCancel} style={{ background: "var(--surface-input)", color: "var(--ink-secondary)", border: "1px solid var(--line)", borderRadius: 8, padding: "8px 16px", fontSize: 13, cursor: "pointer" }}>
            Cancelar
          </button>
          <button onClick={onConfirm} disabled={pending} style={{ background: "var(--danger)", color: "#fff", border: "none", borderRadius: 8, padding: "8px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", opacity: pending ? 0.6 : 1 }}>
            {confirmLabel}
          </button>
        </div>
      </DialogPanel>
    </Overlay>
  );
}
